import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  searchReports43101,
  setDateFrom,
  setDateTo,
  setCommodities,
  setDepositStyles,
  setProjectNames,
  setCompanyNames,
  setReportTypes,
  setReportAuthors,
  setOnlyHumanReviewed,
  clearFilters,
} from "../store/reports43101";
import { useState } from "react";

const commonCommodities = [
  "gold",
  "silver",
  "copper",
  "zinc",
  "lead",
  "nickel",
  "lithium",
  "cobalt",
  "iron",
  "uranium",
  "platinum",
  "palladium",
  "molybdenum",
  "tungsten",
  "tin",
  "rare earths",
];

const FilterInput = ({ label, value, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState("");

  const handleAdd = () => {
    if (inputValue.trim() && !value.includes(inputValue.trim())) {
      onChange([...value, inputValue.trim()]);
      setInputValue("");
    }
  };

  const handleRemove = (item) => {
    onChange(value.filter((v) => v !== item));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm" color="amber.200" fontWeight="semibold">
        {label}
      </FormLabel>
      <Box display="flex" gap={2}>
        <Input
          size="sm"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          bg="slate.700"
          border="1px solid"
          borderColor="slate.600"
          color="white"
          _placeholder={{ color: "slate.400" }}
          _focus={{ borderColor: "amber.400", boxShadow: "0 0 0 1px var(--chakra-colors-amber-400)" }}
          borderRadius="md"
        />
        <Button
          size="sm"
          colorScheme="amber"
          onClick={handleAdd}
          isDisabled={!inputValue.trim()}
        >
          Add
        </Button>
      </Box>
      {value.length > 0 && (
        <Wrap mt={2} spacing={1}>
          {value.map((item, idx) => (
            <WrapItem key={idx}>
              <Tag
                size="sm"
                colorScheme="amber"
                borderRadius="full"
                variant="solid"
              >
                <TagLabel>{item}</TagLabel>
                <TagCloseButton onClick={() => handleRemove(item)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}
    </FormControl>
  );
};

const CommoditySelector = ({ value, onChange }) => {
  const dispatch = useDispatch();

  const toggleCommodity = (commodity) => {
    if (value.includes(commodity)) {
      onChange(value.filter((c) => c !== commodity));
    } else {
      onChange([...value, commodity]);
    }
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm" color="amber.200" fontWeight="semibold">
        Commodities
      </FormLabel>
      <Wrap spacing={2}>
        {commonCommodities.map((commodity) => (
          <WrapItem key={commodity}>
            <Tag
              size="sm"
              cursor="pointer"
              colorScheme={value.includes(commodity) ? "amber" : "gray"}
              variant={value.includes(commodity) ? "solid" : "outline"}
              borderRadius="full"
              onClick={() => toggleCommodity(commodity)}
              _hover={{
                transform: "scale(1.05)",
                shadow: "sm",
              }}
              transition="all 0.2s"
              textTransform="capitalize"
            >
              {commodity}
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
      {value.length > 0 && (
        <Text fontSize="xs" color="slate.400" mt={2}>
          {value.length} selected
        </Text>
      )}
    </FormControl>
  );
};

export const Reports43101Filters = () => {
  const dispatch = useDispatch();
  const dateFrom = useSelector((s) => s.reports43101Slice.dateFrom);
  const dateTo = useSelector((s) => s.reports43101Slice.dateTo);
  const commodities = useSelector((s) => s.reports43101Slice.commodities);
  const depositStyles = useSelector((s) => s.reports43101Slice.depositStyles);
  const projectNames = useSelector((s) => s.reports43101Slice.projectNames);
  const companyNames = useSelector((s) => s.reports43101Slice.companyNames);
  const reportTypes = useSelector((s) => s.reports43101Slice.reportTypes);
  const reportAuthors = useSelector((s) => s.reports43101Slice.reportAuthors);
  const onlyHumanReviewed = useSelector((s) => s.reports43101Slice.onlyHumanReviewed);

  const hasActiveFilters = 
    dateFrom || 
    dateTo || 
    commodities.length > 0 || 
    depositStyles.length > 0 || 
    projectNames.length > 0 || 
    companyNames.length > 0 || 
    reportTypes.length > 0 || 
    reportAuthors.length > 0 ||
    onlyHumanReviewed;

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(searchReports43101());
  };

  const handleFilterChange = (setter) => (value) => {
    dispatch(setter(value));
    dispatch(searchReports43101());
  };

  return (
    <Box color="amber.200">
      <Center>
        <Text
          fontSize="2xl"
          fontWeight="bold"
          bgGradient="linear(to-r, amber.300, amber.500)"
          bgClip="text"
        >
          Filters
        </Text>
      </Center>
      
      {hasActiveFilters && (
        <Center mt={4}>
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={handleClearFilters}
          >
            Clear All Filters
          </Button>
        </Center>
      )}
      
      <Divider mt={6} mb={6} borderColor="slate.600" />
      
      <VStack spacing={6} align="stretch">
        {/* Only Human Reviewed */}
        <FormControl>
          <Checkbox
            isChecked={onlyHumanReviewed}
            onChange={(e) => {
              dispatch(setOnlyHumanReviewed(e.target.checked));
              dispatch(searchReports43101());
            }}
            colorScheme="amber"
            size="md"
          >
            <Text fontSize="sm" color="amber.200" fontWeight="semibold">
              Only Human Reviewed
            </Text>
          </Checkbox>
        </FormControl>
        
        <Divider borderColor="slate.600" />
        
        {/* Date Range */}
        <Box>
          <Text fontSize="sm" color="amber.200" fontWeight="semibold" mb={3}>
            Date Range
          </Text>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="xs" color="slate.400">From</FormLabel>
              <Input
                type="date"
                size="sm"
                value={dateFrom}
                onChange={(e) => {
                  dispatch(setDateFrom(e.target.value));
                  dispatch(searchReports43101());
                }}
                bg="slate.700"
                border="1px solid"
                borderColor="slate.600"
                color="white"
                borderRadius="md"
                _focus={{ borderColor: "amber.400" }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="slate.400">To</FormLabel>
              <Input
                type="date"
                size="sm"
                value={dateTo}
                onChange={(e) => {
                  dispatch(setDateTo(e.target.value));
                  dispatch(searchReports43101());
                }}
                bg="slate.700"
                border="1px solid"
                borderColor="slate.600"
                color="white"
                borderRadius="md"
                _focus={{ borderColor: "amber.400" }}
              />
            </FormControl>
          </VStack>
        </Box>
        
        <Divider borderColor="slate.600" />
        
        {/* Commodities */}
        <CommoditySelector
          value={commodities}
          onChange={handleFilterChange(setCommodities)}
        />
        
        <Divider borderColor="slate.600" />
        
        {/* Deposit Styles */}
        <FilterInput
          label="Deposit Styles"
          value={depositStyles}
          onChange={handleFilterChange(setDepositStyles)}
          placeholder="e.g., VMS, porphyry..."
        />
        
        <Divider borderColor="slate.600" />
        
        {/* Project Names */}
        <FilterInput
          label="Project Names"
          value={projectNames}
          onChange={handleFilterChange(setProjectNames)}
          placeholder="Enter project name..."
        />
        
        <Divider borderColor="slate.600" />
        
        {/* Company Names */}
        <FilterInput
          label="Company Names"
          value={companyNames}
          onChange={handleFilterChange(setCompanyNames)}
          placeholder="Enter company name..."
        />
        
        <Divider borderColor="slate.600" />
        
        {/* Report Types */}
        <FilterInput
          label="Report Types"
          value={reportTypes}
          onChange={handleFilterChange(setReportTypes)}
          placeholder="e.g., Technical Report..."
        />
        
        <Divider borderColor="slate.600" />
        
        {/* Report Authors */}
        <FilterInput
          label="Report Authors"
          value={reportAuthors}
          onChange={handleFilterChange(setReportAuthors)}
          placeholder="Enter author name..."
        />
      </VStack>
    </Box>
  );
};

